import {useEffect,useState} from 'react';

export type SearchPerson={
  kind:'customer'|'team';id:string;displayName:string;
  actorId?:string|null;email?:string|null;phone?:string|null;
  status?:'ACTIVE'|'DISABLED';
};
export type SearchPersonDetails={actorId?:string|null;email?:string|null;phone?:string|null};
type Details=SearchPersonDetails;
type ProfileResponse={ok:boolean;data?:{profile?:{
  actorId?:string|null;email?:string|null;phoneE164?:string|null;phone?:string|null;
}}};

/** One Helm-like search choice, not a profile-page heading.
 * Contact detail hydration for recent IDs requires a fresh authorized lookup;
 * sessionStorage retains identifiers only, never private contact details.
 */
export function SearchPersonRow({person,locationCode,recent=false,onSelect,ariaLabel}:{
  person:SearchPerson;locationCode:string;recent?:boolean;
  onSelect:(details:SearchPersonDetails)=>void;ariaLabel:string;
}){
  const [details,setDetails]=useState<Details>({
    actorId:person.actorId??(person.kind==='team'?person.id:null),
    email:person.email??null,phone:person.phone??null,
  });
  const [photo,setPhoto]=useState<string|null>(null);
  useEffect(()=>{
    setDetails({actorId:person.actorId??(person.kind==='team'?person.id:null),
      email:person.email??null,phone:person.phone??null});
  },[person.actorId,person.email,person.phone,person.id,person.kind]);
  useEffect(()=>{
    // Team Member phones may originate in the owner-maintained employee
    // directory rather than the local profile settings/search index.
    if(!recent&&person.kind!=='team')return;
    const controller=new AbortController();
    const url=person.kind==='customer'?
      '/api/v1/staff/customers/'+encodeURIComponent(person.id):
      '/api/v1/staff/team/'+encodeURIComponent(person.id)+'/profile';
    void fetch(url+'?'+new URLSearchParams({location:locationCode}),{
      credentials:'same-origin',cache:'no-store',signal:controller.signal,
    }).then(async response=>{
      if(!response.ok)throw Error('unavailable');
      const result=await response.json() as ProfileResponse;
      if(!result.ok||!result.data?.profile||controller.signal.aborted)return;
      const profile=result.data.profile;
      setDetails({actorId:person.kind==='team'?person.id:profile.actorId??null,
        email:profile.email??null,phone:profile.phoneE164??profile.phone??null});
    }).catch(()=>{});
    return ()=>controller.abort();
  },[person.id,person.kind,locationCode,recent]);

  const actorId=details.actorId;
  useEffect(()=>{
    setPhoto(null);
    if(!actorId)return;
    const controller=new AbortController();
    let objectUrl:string|null=null;
    void fetch('/api/v1/profiles/'+encodeURIComponent(actorId)+'/avatar',{
      credentials:'same-origin',cache:'no-store',signal:controller.signal,
    }).then(async response=>{
      if(!response.ok)return;
      const blob=await response.blob();
      if(controller.signal.aborted)return;
      objectUrl=URL.createObjectURL(blob);
      setPhoto(objectUrl);
    }).catch(()=>{});
    return ()=>{controller.abort();if(objectUrl)URL.revokeObjectURL(objectUrl);};
  },[actorId]);

  return <button type="button" className="ecfs-search-person" onClick={()=>onSelect(details)}
    aria-label={ariaLabel}>
    <span className="ecfs-search-person-avatar" aria-hidden="true">
      {photo?<img src={photo} alt=""/>:
        <svg viewBox="0 0 40 40" fill="none" stroke="currentColor"
          strokeWidth="1.8" aria-hidden="true">
          <circle cx="20" cy="13" r="7"/>
          <path d="M6 36v-4c0-8 6-12 14-12s14 4 14 12v4"/>
        </svg>}
    </span>
    <span className="ecfs-search-person-identity">
      <strong>{person.displayName}</strong>
      {person.status==='DISABLED'&&<small className="ecfs-search-disabled">Account Disabled</small>}
      {details.email&&<small>{details.email}</small>}
    </span>
    {details.phone&&<span className="ecfs-search-person-phone">
      {details.phone}</span>}
  </button>;
}
